// src/lib/use-photo-upload-queue.js — Phase 4 PR 4 (ADR-076)
//
// Hook custom React qui draine la queue IDB en arrière-plan :
//   - Tente upload Supabase Storage pour chaque photo queued/failed
//   - Retry exponentiel (1s, 5s, 30s) jusqu'à MAX_ATTEMPTS = 3
//   - Au-delà : POST /api/send-email type menage_photo_upload_failed → notif admin
//   - Listen navigator.onLine pour reprendre dès retour réseau
//
// Le hook expose `pending` (count) pour badge UI + onUploaded callback
// pour patcher menage_runs.photos JSONB côté caller.

import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import {
  getAllQueued,
  markUploading,
  markUploaded,
  markFailed,
  markFinalFailed,
  markAlerteEssai,
  markNotified,
  getAlertesEnAttente,
  remettreEnEchecLesBloquees,
  removeFromQueue,
  buildStoragePath,
} from './menage-photo-queue'

const MAX_ATTEMPTS = 3
const RETRY_DELAYS_MS = [1000, 5000, 30000]
const POLL_INTERVAL_MS = 15000 // tick fond toutes les 15s tant qu'il reste des items
// Chantier photo (Lot 1) : les photos de PIÈCE (preuve ménage) vont dans le
// bucket PRIVÉ `menage-photos` (servies en signed URL). Cette file IDB ne traite
// QUE les photos de pièce (kind='piece') ; les photos d'INCIDENT ont leur propre
// flux d'upload immédiat (menage-photo-queue.js), aussi sur `menage-photos`
// depuis le Lot 2 (ADR-209). PUBLIC_BUCKET = branche défensive (aucun item non-piece
// n'est enfilé aujourd'hui).
const PRIVATE_BUCKET = 'menage-photos'
const PUBLIC_BUCKET = 'logements-photos'
const bucketFor = (item) => (item.kind === 'piece' ? PRIVATE_BUCKET : PUBLIC_BUCKET)

// Renvoie { path, publicUrl } : pour le bucket privé, publicUrl=null (on stocke
// le PATH, signé à l'affichage) ; pour le public, l'URL publique.
async function uploadOne(item) {
  const path = buildStoragePath(item)
  const bucket = bucketFor(item)
  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, item.file, { contentType: item.mimeType, upsert: true })
  if (error) throw error
  if (bucket === PRIVATE_BUCKET) return { path, publicUrl: null }
  const { data } = supabase.storage.from(bucket).getPublicUrl(path)
  return { path, publicUrl: data?.publicUrl || null }
}

// Envoie l'alerte admin. Renvoie true SEULEMENT si le courriel est parti :
// réponse OK ET `success` du serveur (api/send-email.js répond 200 avec
// `success:false` quand Resend refuse). Jamais de donnée personnelle : des
// identifiants techniques et le chemin du fichier.
async function notifyAdminFinalFail(item) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      console.error('[photos] alerte admin non envoyée : pas de session')
      return false
    }
    // Endpoint consolidé dans api/send-email.js (Vercel 12/12 saturé)
    // via type='menage_photo_upload_failed'.
    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        type: 'menage_photo_upload_failed',
        data: {
          run_id: item.runId,
          logement_id: item.logementId,
          piece_id: item.pieceId,
          kind: item.kind,
          attempts: item.attempts,
          last_error: item.lastError,
          path: buildStoragePath(item),
          definitif: true,
        },
      }),
    })
    if (!res || !res.ok) {
      console.error('[photos] alerte admin non envoyée : réponse', res?.status)
      return false
    }
    const json = await res.json().catch(() => ({}))
    if (json?.success !== true) {
      console.error('[photos] alerte admin non envoyée : le serveur n\'a pas pu envoyer le courriel')
      return false
    }
    return true
  } catch (e) {
    console.error('[photos] alerte admin non envoyée', e?.message || e)
    return false
  }
}

// Une alerte en attente : on compte l'essai AVANT d'envoyer (au plus
// MAX_ALERTES), et `notifie_le` n'est posé que si l'envoi a réussi.
async function alerter(item) {
  try {
    await markAlerteEssai(item.id)
  } catch (e) {
    console.error('[photos] essai d\'alerte non compté — alerte différée', e?.message || e)
    return
  }
  if (!(await notifyAdminFinalFail(item))) return
  try {
    await markNotified(item.id)
  } catch (e) {
    console.error('[photos] alerte envoyée mais non notée', e?.message || e)
  }
}

// ⚠️ UNE ALERTE PAR PHOTO (lot « travail perdu », relectures). L'état final
// est ÉCRIT DANS LA FILE (IndexedDB) d'abord ; l'alerte part ensuite, depuis
// la liste des alertes en attente (getAlertesEnAttente), et n'est marquée
// envoyée (`notifie_le`) qu'une fois partie. Avant, le marqueur ne vivait
// qu'en mémoire : un courriel partait à chaque passage (15 s).
// Si l'état final ne s'écrit pas, on n'alerte PAS (sinon : boucle) ; on remet
// la photo en échec À PART, pour qu'elle ne reste pas coincée « en cours » :
// le passage suivant retentera.
async function finaliser(item, message) {
  try {
    await markFinalFailed(item.id, message)
  } catch (e) {
    console.error('[photos] état final non écrit — alerte différée', e?.message || e)
    try { await markFailed(item.id, message) }
    catch (e2) { console.error('[photos] photo non remise en échec', e2?.message || e2) }
    return
  }
  await alerter({ ...item, lastError: message })
}

const BLOQUEE_APRES_MS = 2 * 60 * 1000 // « en cours » depuis plus de 2 min = bloquée
const VERROU_MAX_MS = 60 * 1000 // un passage qui ne répond plus libère la file

// ⚠️ COMPTEUR DE DRAINS MONTÉS — le piège ADR-207/208, rendu VISIBLE.
// Deux drains montés en même temps se disputent la même file IndexedDB : les
// photos partent en double, ou disparaissent. Jusqu'ici la règle ne vivait que
// dans des commentaires répartis dans trois fichiers — un refactor la cassait
// sans bruit. Elle vit maintenant au point de passage obligé.
//
// ⚠️ ON N'INTERROMPT PAS. Un drain qui refuserait de se monter ferait perdre
// des photos de terrain — le remède serait pire. On CRIE, fort et nommément :
// le défaut devient visible en développement comme en production.
let _drainsMontes = 0
export function _drainsActuels() { return _drainsMontes }

export function useMenagePhotoUploadQueue({ onUploaded } = {}) {
  const [pending, setPending] = useState(0)
  const [failed, setFailed] = useState(0)
  const onUploadedRef = useRef(onUploaded)
  useEffect(() => { onUploadedRef.current = onUploaded }, [onUploaded])

  useEffect(() => {
    _drainsMontes += 1
    if (_drainsMontes > 1) {
      console.error(
        `[photos] DOUBLE DRAIN : ${_drainsMontes} files de photos montées en même temps. `
        + 'Deux drains se disputent la même file IndexedDB (envois en double ou photos perdues). '
        + 'Un seul écran à la fois doit monter useMenagePhotoUploadQueue — voir ADR-207/208.'
      )
    }
    return () => { _drainsMontes = Math.max(0, _drainsMontes - 1) }
  }, [])

  // Un seul passage à la fois (minuterie, retour réseau, premier plan) : deux
  // passages simultanés liraient le même item avant que l'un n'écrive son
  // état final — deux alertes pour une photo.
  // Le verrou porte son heure de pose : un passage qui ne répond plus (réseau
  // pendu, IndexedDB bloquée) le libère après VERROU_MAX_MS.
  const drainEnCours = useRef(0)
  // Premier passage de CE montage : reprise des photos bloquées.
  const premierPassage = useRef(true)

  const drainUneFois = useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return
    }
    if (premierPassage.current) {
      premierPassage.current = false
      try {
        const n = await remettreEnEchecLesBloquees(BLOQUEE_APRES_MS)
        if (n > 0) console.warn(`[photos] ${n} photo(s) bloquée(s) remise(s) à envoyer`)
      } catch (e) {
        console.error('[photos] reprise des photos bloquées impossible', e?.message || e)
      }
    }
    let queue
    try { queue = await getAllQueued() }
    catch (e) {
      console.error('[photos] lecture de la file impossible', e?.message || e)
      return
    }
    setPending(queue.filter(it => it.status === 'queued' || it.status === 'uploading').length)
    setFailed(queue.filter(it => it.status === 'failed').length)
    // Alertes d'échec définitif pas encore parties (envoi raté à un passage
    // précédent) : on les renvoie AVANT de traiter la file — celles qui
    // deviennent définitives pendant ce passage partent dans finaliser().
    try {
      for (const it of await getAlertesEnAttente()) await alerter(it)
    } catch (e) {
      console.error('[photos] lecture des alertes en attente impossible', e?.message || e)
    }
    for (const item of queue) {
      if (item.attempts >= MAX_ATTEMPTS) {
        // Final fail : état écrit DANS la file, puis UNE alerte admin.
        await finaliser(item, `Final fail after ${item.attempts} attempts: ${item.lastError}`)
        continue
      }
      // Backoff exponentiel
      const delay = RETRY_DELAYS_MS[Math.min(item.attempts, RETRY_DELAYS_MS.length - 1)]
      const lastTry = item.lastAttemptAt ? new Date(item.lastAttemptAt).getTime() : 0
      if (Date.now() - lastTry < delay) continue

      try {
        await markUploading(item.id)
        const { path, publicUrl } = await uploadOne(item)
        await markUploaded(item.id, publicUrl)
        // Le fichier est dans le Storage, mais la photo n'existe pour le ménage
        // qu'une fois ENREGISTRÉE dans menage_runs.photos (onUploaded). On ne
        // retire l'item de la file QUE si cet enregistrement a réussi. S'il
        // échoue (réseau, ménage verrouillé 48 h…), l'erreur remonte au catch
        // ci-dessous → markFailed : l'item repasse 'failed', il sera retenté
        // (ré-envoi idempotent, upsert sur le même chemin) jusqu'à MAX_ATTEMPTS,
        // puis signalé à l'admin. Sans enregistreur branché, on ne retire rien
        // non plus : personne n'a attaché la photo au ménage.
        if (typeof onUploadedRef.current !== 'function') {
          throw new Error('Aucun enregistrement de photo branché : photo gardée dans la file')
        }
        await onUploadedRef.current({ ...item, path, publicUrl })
        await removeFromQueue(item.id)
      } catch (e) {
        if (e && e.definitif) {
          // Refus définitif (0 ligne : verrou 48 h ou ménage réaffecté ;
          // ménage introuvable) : retenter ne changera rien → alerte unique.
          await finaliser(item, e.message || String(e))
        } else {
          await markFailed(item.id, e.message || String(e))
        }
      }
    }
    // Refresh counts post-tick
    try {
      const after = await getAllQueued()
      setPending(after.filter(it => it.status === 'queued' || it.status === 'uploading').length)
      setFailed(after.filter(it => it.status === 'failed').length)
    } catch (_) {}
  }, [])

  const drain = useCallback(async () => {
    const pose = drainEnCours.current
    if (pose && Date.now() - pose < VERROU_MAX_MS) return
    if (pose) console.error('[photos] passage précédent bloqué depuis plus de 60 s — verrou libéré')
    const jeton = Date.now() || 1
    drainEnCours.current = jeton
    let minuterie = null
    let fini = false
    const p = drainUneFois().finally(() => { fini = true })
    try {
      // On n'attend jamais plus que la limite : la minuterie de fond continue.
      await Promise.race([
        p,
        new Promise((r) => { minuterie = setTimeout(r, VERROU_MAX_MS) }),
      ])
    } catch (e) {
      console.error('[photos] passage en erreur', e?.message || e)
    } finally {
      if (minuterie) clearTimeout(minuterie)
      // Un passage tardif ne libère pas le verrou d'un passage plus récent.
      const liberer = () => { if (drainEnCours.current === jeton) drainEnCours.current = 0 }
      if (fini) liberer()
      else {
        p.then(liberer, (e) => {
          liberer()
          console.error('[photos] passage tardif en erreur', e?.message || e)
        })
      }
    }
  }, [drainUneFois])

  useEffect(() => {
    let mounted = true
    let timer = null
    const tick = async () => {
      if (!mounted) return
      await drain()
      timer = setTimeout(tick, POLL_INTERVAL_MS)
    }
    tick()
    const onOnline = () => { drain() }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline)
      window.addEventListener('focus', onOnline)
    }
    return () => {
      mounted = false
      if (timer) clearTimeout(timer)
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline)
        window.removeEventListener('focus', onOnline)
      }
    }
  }, [drain])

  return { pending, failed, drainNow: drain }
}
